import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, ExternalLink, Trophy } from 'lucide-react';
import { SiX } from '@icons-pack/react-simple-icons';
import { message, result, createDataItemSigner } from '@permaweb/aoconnect';
import { formatDate } from '../../utils/formatters';
import PremiumButton from '../ui/PremiumButton';

// X Posts Modal Component
const XPostsModal = ({ isOpen, onClose, userData, processId }) => {
  const [xPosts, setXPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchXPosts = useCallback(async () => {
    if (!userData?.userId || !window.arweaveWallet) return;
    setLoading(true);
    setError("");
    try {
      const signer = createDataItemSigner(window.arweaveWallet);
      const timeout = 10000;
      const msgId = await message({
        process: processId,
        signer: signer,
        tags: [
          { name: "Action", value: "Get-X-Postings" },
          { name: "User-ID", value: userData.userId }
        ],
        data: ""
      });

      const started = Date.now();
      while (Date.now() - started < timeout) {
        const res = await result({ message: msgId, process: processId });
        if (res?.Messages?.length) {
          try {
            const payload = JSON.parse(res.Messages[0].Data);
            if (payload.Success === false) throw new Error(payload.message);
            setXPosts(payload.postings || []);
            break;
          } catch (e) {
            throw new Error(`Malformed response: ${e.message}`);
          }
        }
        await new Promise(r => setTimeout(r, 900));
      }

      if (Date.now() - started >= timeout) {
        throw new Error("Timed-out waiting for Get-X-Postings response");
      }
    } catch (err) {
      setError(`Failed to fetch X posts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [userData?.userId, processId]);

  useEffect(() => {
    if (isOpen) {
      fetchXPosts();
    }
  }, [isOpen, fetchXPosts]);

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden
                   bg-gradient-to-br from-[#0A0E1B]/95 via-[#0D1220]/90 to-[#0A0E1B]/95
                   rounded-3xl backdrop-blur-xl
                   border border-white/10"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SiX className="w-6 h-6 text-[#1DA1F2]" />
              <h3 className="text-2xl font-bold text-[#E8FDF2]">X Platform Posts</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white/5 transition-colors"
            >
              <X className="w-6 h-6 text-[#8AA5A2]" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#15FF75] border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-[#8AA5A2]">Loading posts...</span>
            </div>
          )}

          {error && (
            <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-400 text-center">
              {error}
            </div>
          )}

          {!loading && !error && xPosts.length === 0 && (
            <div className="text-center py-12 text-[#8AA5A2]">
              <SiX className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <div className="font-medium text-[#E8FDF2]">No X posts found</div>
              <div className="text-sm mt-1">Start posting to see your activity here!</div>
            </div>
          )}

          {!loading && !error && xPosts.length > 0 && (
            <div className="space-y-4">
              {xPosts.map((post, index) => (
                <motion.div
                  key={index}
                  className="p-6 bg-gradient-to-br from-[#0A0E1B]/60 to-[#0D1220]/40 rounded-2xl border border-white/5"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <SiX className="w-5 h-5 text-[#1DA1F2]" />
                      <span className="text-[#E8FDF2] font-medium">
                        {formatDate(post.timestamp)}
                      </span>
                    </div>
                    {post.transactionId && (
                      <PremiumButton
                        variant="ghost"
                        disabled={false}
                        size="sm"
                        onClick={() => window.open(`https://ao.link/#/message/${post.transactionId}`, '_blank')}
                        icon={<ExternalLink className="w-3 h-3" />}
                      >
                        Verify
                      </PremiumButton>
                    )}
                  </div>
                  
                  <div className="text-[#8AA5A2] mb-4">
                    {post.content || "Post content not available"}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-[#15FF75]" />
                      <span className="text-[#8AA5A2]">
                        +{post.pointsEarned || 0} points
                      </span>
                    </div>
                    {post.url && (
                      <a
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#1DA1F2] hover:text-[#1DA1F2]/80 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Post</span>
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default XPostsModal;